import React, { useState, useEffect } from "react";
import { Typography, Grid, Container } from "@mui/material";
import api from "../../globals/axios";
import LocateLand from "../../components/LocateLand";
import { useParams } from "react-router-dom";
import CustomAppBar from "../../components/AppBar";
import LoaderContainer from "../../components/LoaderContainer";
import DownLoadIcon from "../../assets/Icon/DownLoadIcon.png";
import ViewIcon from "../../assets/Icon/ViewIcon.png";
import CustomCardView from "../../components/Custom/CardView";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import {
  CustomLabel,
  CustomValue,
} from "../../components/Custom/CustomTypography";
import { useTranslation } from "react-i18next";
import { acresToHectares } from "../../globals/utils";
import { LoadScript } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY } from "../../globals/const";

export default function ViewFarm() {
  const { t } = useTranslation();
  const { farmId } = useParams();
  const [loading, setLoading] = useState(true);
  const [tokenData, setTokenData] = useState({
    farm_name: "",
    area: "",
    regulator: "",
    pin_code: "",
    irrigation_source: "",
    crops: [],
    polygon: [],
  });
  const [location, setLocation] = useState({});
  const [path, setPath] = useState([{ lat: 0, lng: 0 }]);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  const labelValuePairs = [
    { label: t("Farm Name"), value: tokenData.farm_name },
    {
      label: t("Area"),
      value: `${tokenData.area} ${t("acres")} (${acresToHectares(
        tokenData.area
      )} ${t("hectares")})`,
    },
    { label: t("Land Record Number"), value: tokenData.land_record_number },
    { label: t("PIN Code"), value: tokenData.pin_code },
    {
      label: t("Irrigation Source"),
      value: tokenData.irrigation_source?.[0]?.name
        ? tokenData.irrigation_source?.map((source) => source.name).join(", ")
        : "N/A",
    },
    {
      label: t("Irrigation System"),
      value: tokenData.irrigation_system?.[0]?.name
        ? tokenData.irrigation_system?.map((system) => system.name).join(", ")
        : "N/A",
    },
    {
      label: t("Soil Texture"),
      value: tokenData.soil_texture?.[0]?.name ?? "N/A",
    },
    { label: t("Owner Name"), value: tokenData.owner_name },
    {
      label: t("Latitude"),
      value: tokenData.polygon.length > 0 ? `${tokenData.polygon[0].lat}` : "",
    },
    {
      label: t("Longitude"),
      value: tokenData.polygon.length > 0 ? ` ${tokenData.polygon[0].lng}` : "",
    },
  ];
  const loadNFT = async () => {
    try {
      const res = await api.get(`/farmer/farms/${farmId}`);
      const data = res.data;
      setPath(data.polygon);
      setTokenData(data);
      setLocation({
        latitude: data.polygon[0]["lat"],
        longitude: data.polygon[0]["lng"],
      });
      setLoading(false);
    } catch (error) {
      console.error("Error loading farm data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNFT();
  }, [farmId]);

  return (
    <>
      <CustomAppBar isProtected={false} />
      <LoaderContainer loading={loading}>
        <Container maxWidth="xs" sx={{ padding: "16px" }}>
          <CustomTitle>{t("Farm View")}</CustomTitle>
          <Grid
            container
            direction="column"
            style={{ marginTop: "10px" }}
            spacing={0}
          >
            {labelValuePairs.map((pair) => (
              <Grid item container key={pair.label}>
                <Grid item xs={5}>
                  <CustomLabel>{pair.label}</CustomLabel>
                </Grid>
                <Grid item xs={1}>
                  <CustomLabel>:</CustomLabel>
                </Grid>

                <Grid item xs={6}>
                  <CustomValue>{pair.value}</CustomValue>
                </Grid>
              </Grid>
            ))}

            {location["latitude"] && (
              <div style={{ marginTop: "20px" }}>
                <CustomLabel> {t("Farm Map Location")}</CustomLabel>
                <LoadScript
                  googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                  onLoad={() => setIsScriptLoaded(true)}
                >
                  <LocateLand
                    center={{
                      lat: location["latitude"],
                      lng: location["longitude"],
                    }}
                    setCenter={() => {}}
                    path={path}
                    setPath={() => {}}
                    editable={false}
                    setCurrentLocation={() => {}}
                    isScriptLoaded={isScriptLoaded}
                  />
                </LoadScript>
              </div>
            )}

            {/* Farm Document Text */}

            <Grid item xs={12}>
              <CustomLabel>{t("Legal Document")}:</CustomLabel>
            </Grid>
            <Grid container item xs={9} alignItems="center">
              <Grid item xs={9}>
                <CustomValue>
                  {tokenData.farm_doc ? (
                    <Typography style={{ fontSize: "12px" }}>
                      {tokenData.farm_doc.split("/").pop().split("?")[0]}
                    </Typography>
                  ) : (
                    t("No farm document available")
                  )}
                </CustomValue>
              </Grid>
              {tokenData.farm_doc && (
                <>
                  <Grid item xs={1} columnGap={2} sx={{ marginTop: "10px" }}>
                    <a href={tokenData.farm_doc} target="_blank" rel="noreferrer">
                      <img src={ViewIcon} alt="View Icon" />
                    </a>
                  </Grid>
                  <Grid
                    item
                    xs={1}
                    sx={{ marginLeft: "20px", marginTop: "10px" }}
                    // onClick={handleDownload}
                  >
                    <img src={DownLoadIcon} alt="Download Icon" />
                  </Grid>
                </>
              )}
            </Grid>

            {/* Card Section for Crop Information */}

            <div style={{ overflowY: "auto", maxHeight: "180px" }}>
              {/* Adjust maxHeight according to your needs */}
              <Grid item style={{ marginTop: "20px", marginBottom: "20px" }}>
                {tokenData.crops.length > 0 && <CustomLabel>Crops</CustomLabel>}
                {tokenData.crops.map((crop, index) => (
                  <CustomCardView
                    key={index}
                    crop={crop}
                    dialogData={[
                      { label: t("Farm Name"), value: tokenData.farm_name },
                      { label: t("Crop Type"), value: crop.commodity.name },
                      { label: t("Sowing Date"), value: crop.sowing_date },
                      { label: t("Seed Type"), value: crop.seed_type },
                      { label: t("Seed Source"), value: crop.seed_source },
                      { label: t("Seed Quantity"), value: crop.seed_quantity },
                      {
                        label: t("Estimated Harvest"),
                        value: crop.estimated_harvest,
                      },
                      {
                        label: t("Estimated Harvest Date"),
                        value: crop.estimated_harvest_date,
                      },
                    ]}
                  />
                ))}
              </Grid>
            </div>
          </Grid>
        </Container>
      </LoaderContainer>
    </>
  );
}
